import { useState } from "react";
import { COLORS, BG_BASE } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";

const sampleData = {
  score: 8.7,
  fitAssessment: "Strong",
  engagementContext: { type: "Cloud Migration", industry: "Financial Services", criticalDISC: "High Conscientiousness (compliance, accuracy), Moderate Dominance (decisiveness)" },
  members: [
    { name: "Todd Korando", role: "DevOps Engineer", type: "C/D", D: 8, I: 4, S: 4, C: 8, style: "Professional collaborator" },
    { name: "Shirley Cortez", role: "SQL Developer", type: "I/D", D: 8, I: 10, S: 4, C: 4, style: "Highly collaborative and social" },
    { name: "Michael Monske", role: "SRE", type: "S/C", D: 4, I: 4, S: 9, C: 7, style: "Team-oriented and supportive" },
  ],
  criteria: [
    { name: "Role-Critical DISC", score: 7, detail: "C≥6: 2/3 members (Korando:8, Monske:7)" },
    { name: "Decision-Making", score: 8, detail: "D≥7: 2/3 members (Korando:8, Cortez:8)" },
    { name: "Style Diversity", score: 8, detail: "Pace: Korando, Cortez | Precision: Monske" },
    { name: "Collab. Mitigant", score: 1, detail: "High-D pair mitigated by shared history", isBonus: true },
  ],
  dynamics: [
    { type: "positive", text: "Korando (C/D) brings structured decisiveness — ideal for migration planning" },
    { type: "positive", text: "Cortez (I/D) is the team's communicator — high I (10) for client interactions" },
    { type: "positive", text: "Monske (S/C) provides steady execution — critical for SRE reliability work" },
    { type: "warning", text: "Two high-D members (Korando + Cortez) — mitigated by shared engagement history" },
  ],
};

function DISCBar({ value, max = 10, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 60, height: 6, background: BG_BASE, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${(value / max) * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, color: COLORS.textMuted, minWidth: 14 }}>{value}</span>
    </div>
  );
}

export default function DISCEngagementFit({ data, onEmployeeClick }) {
  const d = data || sampleData;
  const fitColor = d.score >= 7 ? COLORS.green : d.score >= 5 ? COLORS.amber : COLORS.red;

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>DISC Engagement Fit</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: fitColor }}>{(d.score || 0).toFixed(1)}</span>
          <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${fitColor}20`, color: fitColor }}>{d.fitAssessment?.toUpperCase()}</span>
        </div>
      </div>

      {d.engagementContext && (
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 16, padding: "6px 10px", background: BG_BASE, borderRadius: 4 }}>
          <strong style={{ color: COLORS.text }}>{d.engagementContext.type}</strong> ({d.engagementContext.industry}) — Needs: {d.engagementContext.criticalDISC}
        </div>
      )}

      {/* Team DISC table */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Team DISC Profiles</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
              {["Member", "Type", "Dominance", "Influence", "Steadiness", "Conscientiousness", "Collaboration Style"].map(h => (
                <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: COLORS.textMuted, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(d.members || []).map((m, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${COLORS.cardBorder}15` }}>
                <td style={{ padding: "8px" }}>
                  <div style={{ fontWeight: 500 }}>
                    <ClickableName name={m.name} onClick={onEmployeeClick} />
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{m.role}</div>
                </td>
                <td style={{ padding: "8px" }}>
                  <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: `${COLORS.purple}20`, color: COLORS.purple }}>{m.type}</span>
                </td>
                <td style={{ padding: "8px" }}><DISCBar value={m.D} color={COLORS.red} /></td>
                <td style={{ padding: "8px" }}><DISCBar value={m.I} color={COLORS.amber} /></td>
                <td style={{ padding: "8px" }}><DISCBar value={m.S} color={COLORS.green} /></td>
                <td style={{ padding: "8px" }}><DISCBar value={m.C} color={COLORS.blue} /></td>
                <td style={{ padding: "8px", fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>{m.style}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Criteria grid */}
      {(d.criteria || []).length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {d.criteria.map((c, i) => (
            <div key={i} style={{ background: BG_BASE, borderRadius: 6, padding: "10px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{c.name}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: c.isBonus ? COLORS.teal : c.score >= 7 ? COLORS.green : c.score >= 5 ? COLORS.amber : COLORS.red }}>
                  {c.isBonus ? `+${(c.score || 0).toFixed(1)}` : `${(c.score || 0).toFixed(1)}`}
                </span>
              </div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.4 }}>{c.detail}</div>
            </div>
          ))}
        </div>
      )}

      {/* Key dynamics */}
      {(d.dynamics || []).length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Key Dynamics</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.dynamics.map((dyn, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 10px", borderRadius: 4, background: dyn.type === "warning" ? `${COLORS.amber}08` : `${COLORS.green}08`, borderLeft: `3px solid ${dyn.type === "warning" ? COLORS.amber : COLORS.green}` }}>
                <span style={{ color: dyn.type === "warning" ? COLORS.amber : COLORS.green, fontSize: 12, flexShrink: 0, marginTop: 1 }}>{dyn.type === "warning" ? "⚠" : "✓"}</span>
                <span style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>{dyn.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
